import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import logo from "../../../assets/website/images/logo.png";

function WebsiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const location = useLocation();
  const navigate = useNavigate();

  const links = useMemo(
    () => [
      { id: "about", label: "About us" },
      { id: "how-it-works", label: "How it works" },
      { id: "contact", label: "Contact us" },
    ],
    []
  );

  useEffect(() => {
    if (location.pathname !== "/") return;

    const sections = links
      .map((l) => document.getElementById(l.id))
      .filter(Boolean);

    function onScroll() {
      const fromTop = window.scrollY + 120;
      let current = sections[0]?.id;

      sections.forEach((section) => {
        if (section.offsetTop <= fromTop) current = section.id;
      });

      if (current) setActiveSection(current);
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname, links]);

  function goToSection(id) {
    setMobileOpen(false);

    if (id === "contact") {
      navigate("/contact-us");
      return;
    }

    if (location.pathname !== "/") {
      sessionStorage.setItem("website_scroll_to", id);
      navigate("/");
      return;
    }

    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  useEffect(() => {
    if (location.pathname !== "/") return;
    const pending = sessionStorage.getItem("website_scroll_to");
    if (!pending) return;
    sessionStorage.removeItem("website_scroll_to");
    // Wait a tick so DOM is ready.
    setTimeout(() => {
      const el = document.getElementById(pending);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }, [location.pathname]);

  return (
    <header className="w-full bg-white py-4 px-6 sm:px-10 lg:px-16">
      <div className="container">
        <div className="flex items-center justify-between">
          <a
            href="#home"
            className="relative h-12 w-[89px] overflow-hidden"
            onClick={(e) => {
              e.preventDefault();
              goToSection("home");
            }}
          >
            <img src={logo} alt="Online Closet logo" className="h-full w-full object-contain" />
          </a>

          {/* Mobile hamburger */}
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md border border-border p-2 text-popover-foreground lg:hidden"
            aria-label="Toggle navigation"
            aria-controls="mobile-nav"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm1 4a1 1 0 100 2h12a1 1 0 100-2H4z" clipRule="evenodd" />
            </svg>
          </button>

          {/* Desktop nav */}
          <div className="hidden items-center gap-10 lg:flex">
            <nav aria-label="Primary navigation">
              <ul className="flex items-center justify-between gap-6">
                {links.map((l) => {
                  const isActive = (l.id === "contact" && location.pathname === "/contact-us") || (location.pathname === "/" && activeSection === l.id);
                  return (
                    <li key={l.id}>
                      <a
                        href={`#${l.id}`}
                        data-scrollspy-link
                        onClick={(e) => {
                          e.preventDefault();
                          goToSection(l.id);
                        }}
                        className={`text-base font-medium leading-[22px] transition-colors hover:text-primary ${
                          isActive ? "text-primary font-semibold" : "text-popover-foreground"
                        }`}
                      >
                        {l.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </nav>
        <a
  href="#download-app"
  data-scrollspy-link
  onClick={(e) => {
    e.preventDefault();
    goToSection("download-app");
  }}
  style={{
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "130px",
    height: "54px",
    padding: "0 15px",
    gap: "5px",
    marginLeft:"40px",
    borderRadius: "10px",
    textDecoration: "none",
    fontWeight: "500",
    fontSize: "14px",
    color: "#fff",
    background: "linear-gradient(90deg, #7100D7, #F116A5)",
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s ease"
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.opacity = "0.9";
    e.currentTarget.style.transform = "translateY(-1px)";
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.opacity = "1";
    e.currentTarget.style.transform = "translateY(0)";
  }}
>
  Download App
</a>
          </div>
        </div>

        {/* Mobile nav */}
        <div id="mobile-nav" className={`pt-4 lg:hidden ${mobileOpen ? "" : "hidden"}`}>
          <nav aria-label="Mobile navigation">
            <ul className="flex flex-col gap-3">
              {links.map((l) => (
                <li key={l.id}>
                  <a
                    href={`#${l.id}`}
                    data-scrollspy-link
                    className="block text-base font-medium leading-[22px] text-popover-foreground transition-colors hover:text-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      goToSection(l.id);
                    }}
                  >
                    {l.label}
                  </a>
                </li>
              ))}
              <li className="pt-2">
                      <a
  href="#download-app"
  data-scrollspy-link
  onClick={(e) => {
    e.preventDefault();
    goToSection("download-app");
  }}
  style={{
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "130px",
    height: "34px",
    padding: "0 15px",
    gap: "5px",
    borderRadius: "10px",
    textDecoration: "none",
    fontWeight: "500",
    fontSize: "14px",
    color: "#fff",
    background: "linear-gradient(90deg, #7100D7, #F116A5)",
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s ease"
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.opacity = "0.9";
    e.currentTarget.style.transform = "translateY(-1px)";
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.opacity = "1";
    e.currentTarget.style.transform = "translateY(0)";
  }}
>
  Download App
</a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </header>
  );
}

export default WebsiteHeader;